import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { fetchUserBootstrap } from "@/lib/user-bootstrap";
import { supabase } from "@/integrations/supabase/client";

interface FeatureFlags {
  [key: string]: boolean;
}

// Cache em módulo: flags raramente mudam dentro da sessão. Evita skeleton
// piscando em telas que dependem de flags em cada remount.
let cache: { flags: FeatureFlags; ts: number; userId: string | null } | null = null;
const TTL_MS = 5 * 60_000;

export const useFeatureFlags = () => {
  const { user } = useAuth();
  const matchesUser = cache && cache.userId === (user?.id ?? null);
  const fresh = matchesUser && cache && Date.now() - cache.ts < TTL_MS ? cache : null;
  const [flags, setFlags] = useState<FeatureFlags>(fresh?.flags ?? (matchesUser ? cache!.flags : {}));
  const [loading, setLoading] = useState(!fresh);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (user) {
          const bootstrap = await fetchUserBootstrap(user.id);
          if (!active) return;
          setFlags(bootstrap.feature_flags);
          cache = { flags: bootstrap.feature_flags, ts: Date.now(), userId: user.id };
          return;
        }
        const { data } = await supabase
          .from("feature_flags" as any)
          .select("key, enabled");
        if (!active) return;
        const map: FeatureFlags = {};
        ((data as any[]) ?? []).forEach((f: any) => {
          map[f.key] = f.enabled;
        });
        setFlags(map);
        cache = { flags: map, ts: Date.now(), userId: null };
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [user]);

  const isEnabled = (key: string, defaultValue = true): boolean => {
    return flags[key] ?? defaultValue;
  };

  return { flags, loading, isEnabled };
};
